/**
 * Add Health Record Page - Create new health records for cattle
 * Mapped to backend HealthRecordDTO with exact field names
 */

import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Calendar, FileText, Search, ChevronDown, Heart } from 'lucide-react';
import {
  healthRecordsApi,
  cattleApi,
  type HealthRecord,
  type Cattle
} from '../../services/gaushala/api';
import { getLoggedInUserGaushalaId } from '../../utils/auth';
import { API_URLS } from '../../lib/apiConfig';
import { offlineHealthRecordApi, offlineCattleApi } from '../../lib/offline/offlineApi';

interface HealthRecordFormData {
  cattleId: number;
  recordType: string;
  recordDate: string;
  veterinarianName: string;
  veterinarianLicense: string;
  veterinarianContact: string;
  diagnosis: string;
  treatment: string;
  medications: string;
  dosageInstructions: string;
  vaccinationType: string;
  nextVaccinationDate: string;
  nextCheckupDate: string;
  cost: number;
  notes: string;
  performedBy: string;
  status: string;
}

// InputField Component - defined outside to prevent recreation
const InputField = memo(({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required = false,
  error,
  maxLength,
  className = '',
  isPhoneNumber = false,
  ...props
}: any) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    if (isPhoneNumber) {
      // Only allow digits and limit to 10 characters
      newValue = newValue.replace(/[^0-9]/g, '').slice(0, 10);
    }
    if (type === 'number') {
      onChange(Number(newValue));
    } else {
      onChange(newValue);
    }
  }, [type, isPhoneNumber, onChange]);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-800">
        {label} {required && <span className="text-red-500">*</span>}
        {maxLength && (
          <span className="text-xs text-gray-500 font-normal ml-2">
            ({String(value || '').length}/{maxLength})
          </span>
        )}
      </label>
      <input
        type={type}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300'
        }`}
        {...props}
      />
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );
});
InputField.displayName = 'InputField';

// TextAreaField Component - defined outside to prevent recreation
const TextAreaField = memo(({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error,
  rows = 3,
  maxLength,
  className = ''
}: any) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-800">
        {label} {required && <span className="text-red-500">*</span>}
        {maxLength && (
          <span className="text-xs text-gray-500 font-normal ml-2">
            ({String(value || '').length}/{maxLength})
          </span>
        )}
      </label>
      <textarea
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 resize-vertical ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300'
        }`}
      />
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );
});
TextAreaField.displayName = 'TextAreaField';

// CostField Component - defined outside to prevent recreation
const CostField = memo(({
  label,
  value,
  onChange,
  error,
  className = ''
}: any) => {
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value) || 0);
  }, [onChange]);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-800">
        {label}
      </label>

      <div className="relative flex items-center">
        <input
          type="number"
          value={value || 0}
          onChange={handleInputChange}
          placeholder="0"
          min="0"
          step="1"
          className={`w-full px-3 py-2 pl-8 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent hover:border-gray-400 ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-300'
          }`}
        />

        <span className="absolute left-3 text-lg font-semibold text-gray-600">₹</span>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 mt-2">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );
});
CostField.displayName = 'CostField';

export default function AddHealthRecord() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Dropdown data from backend APIs
  const [cattle, setCattle] = useState<{ id: number; name: string }[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [gaushalaId, setGaushalaId] = useState<number | null>(null);

  // Field validation rules with min/max lengths
  const VALIDATION_RULES = {
    veterinarianName: { min: 2, max: 100 },
    veterinarianLicense: { min: 2, max: 50 },
    veterinarianContact: { min: 10, max: 10 },
    diagnosis: { min: 5, max: 500 },
    treatment: { min: 5, max: 500 },
    medications: { min: 5, max: 500 },
    dosageInstructions: { min: 5, max: 500 },
    vaccinationType: { min: 2, max: 50 },
    performedBy: { min: 2, max: 100 },
    notes: { min: 0, max: 1000 }
  };

  const [formData, setFormData] = useState<HealthRecordFormData>({
    cattleId: 0,
    recordType: '',
    recordDate: '',
    veterinarianName: '',
    veterinarianLicense: '',
    veterinarianContact: '',
    diagnosis: '',
    treatment: '',
    medications: '',
    dosageInstructions: '',
    vaccinationType: '',
    nextVaccinationDate: '',
    nextCheckupDate: '',
    cost: 0,
    notes: '',
    performedBy: '',
    status: ''
  });

  const [errors, setErrors] = useState<Partial<Record<keyof HealthRecordFormData, string>>>({});

  useEffect(() => {
    // Get gaushala ID from logged-in user context
    let userGaushalaId = getLoggedInUserGaushalaId();

    // If gaushala ID not found in token, we'll fetch it from the gaushala access endpoint
    if (!userGaushalaId) {
      console.warn('Gaushala ID not found in JWT token, will be fetched from API');
    }

    setGaushalaId(userGaushalaId);

    // Fetch cattle from backend API
    const fetchDropdownData = async () => {
      setDataLoading(true);
      try {
        // First, try to get gaushala ID if not already set
        if (!userGaushalaId) {
          try {
            const userContext = await import('../../utils/auth').then(m => m.getCurrentUserContext());
            const userId = userContext.userId;

            // Call gaushala service to get user's primary gaushala
            const response = await fetch(
              `${API_URLS.gaushala}/api/v1/gaushala/user-access/primary?userId=${userId}`,
              {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                }
              }
            );

            if (response.ok) {
              const gaushalaIdFromApi = await response.json();
              if (gaushalaIdFromApi) {
                userGaushalaId = gaushalaIdFromApi;
                setGaushalaId(gaushalaIdFromApi);
                console.log('Fetched gaushala ID from API:', gaushalaIdFromApi);
              }
            }
          } catch (error) {
            console.warn('Failed to fetch gaushala ID from API:', error);
          }
        }

        // Use OFFLINE API as primary - it fetches from server AND caches automatically
        // When offline, it returns cached data
        console.log('📡 [HEALTH RECORD] Fetching cattle via offline-aware API...');
        const cattleData = await offlineCattleApi.getAll(0, 1000);

        if (cattleData && cattleData.length > 0) {
          const cattleList = cattleData.map((c: any) => ({
            id: c.id,
            name: `${c.uniqueAnimalId || 'Unknown'} - ${c.name || 'Unnamed'}`
          }));
          setCattle(cattleList);
          console.log(`✅ [HEALTH RECORD] Loaded ${cattleList.length} cattle`);
        } else {
          setMessage({
            type: 'error',
            text: 'No cattle data available. Please check your connection.'
          });
        }
      } catch (error) {
        console.error('Error fetching cattle data:', error);
        setMessage({
          type: 'error',
          text: 'Failed to load cattle data. Please refresh the page.'
        });
      } finally {
        setDataLoading(false);
      }
    };

    fetchDropdownData();
  }, []);

  const handleInputChange = useCallback((field: keyof HealthRecordFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
      return prev;
    });
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof HealthRecordFormData, string>> = {};

    // Required field validations
    if (formData.cattleId === 0) newErrors.cattleId = 'Cattle is required';
    if (!formData.recordType.trim()) newErrors.recordType = 'Record Type is required';
    if (!formData.recordDate.trim()) newErrors.recordDate = 'Record Date is required';
    if (!formData.status.trim()) newErrors.status = 'Status is required';

    // Veterinarian Name validation
    if (formData.veterinarianName) {
      const nameLen = formData.veterinarianName.trim().length;
      if (nameLen < VALIDATION_RULES.veterinarianName.min) {
        newErrors.veterinarianName = `Veterinarian Name must be at least ${VALIDATION_RULES.veterinarianName.min} characters`;
      } else if (nameLen > VALIDATION_RULES.veterinarianName.max) {
        newErrors.veterinarianName = `Veterinarian Name cannot exceed ${VALIDATION_RULES.veterinarianName.max} characters`;
      }
    }

    // Veterinarian License validation
    if (formData.veterinarianLicense) {
      const licLen = formData.veterinarianLicense.trim().length;
      if (licLen < VALIDATION_RULES.veterinarianLicense.min) {
        newErrors.veterinarianLicense = `License must be at least ${VALIDATION_RULES.veterinarianLicense.min} characters`;
      } else if (licLen > VALIDATION_RULES.veterinarianLicense.max) {
        newErrors.veterinarianLicense = `License cannot exceed ${VALIDATION_RULES.veterinarianLicense.max} characters`;
      }
    }

    // Veterinarian Contact validation (10-digit number only)
    if (formData.veterinarianContact) {
      const contactValue = formData.veterinarianContact.trim();
      if (!/^\d{10}$/.test(contactValue)) {
        newErrors.veterinarianContact = 'Contact must be exactly 10 digits';
      }
    }

    // Diagnosis validation
    if (formData.diagnosis) {
      const diagLen = formData.diagnosis.trim().length;
      if (diagLen < VALIDATION_RULES.diagnosis.min) {
        newErrors.diagnosis = `Diagnosis must be at least ${VALIDATION_RULES.diagnosis.min} characters`;
      } else if (diagLen > VALIDATION_RULES.diagnosis.max) {
        newErrors.diagnosis = `Diagnosis cannot exceed ${VALIDATION_RULES.diagnosis.max} characters`;
      }
    }

    // Treatment validation
    if (formData.treatment) {
      const treatLen = formData.treatment.trim().length;
      if (treatLen < VALIDATION_RULES.treatment.min) {
        newErrors.treatment = `Treatment must be at least ${VALIDATION_RULES.treatment.min} characters`;
      } else if (treatLen > VALIDATION_RULES.treatment.max) {
        newErrors.treatment = `Treatment cannot exceed ${VALIDATION_RULES.treatment.max} characters`;
      }
    }

    // Medications validation
    if (formData.medications) {
      const medLen = formData.medications.trim().length;
      if (medLen < VALIDATION_RULES.medications.min) {
        newErrors.medications = `Medications must be at least ${VALIDATION_RULES.medications.min} characters`;
      } else if (medLen > VALIDATION_RULES.medications.max) {
        newErrors.medications = `Medications cannot exceed ${VALIDATION_RULES.medications.max} characters`;
      }
    }

    // Dosage Instructions validation
    if (formData.dosageInstructions) {
      const dosageLen = formData.dosageInstructions.trim().length;
      if (dosageLen < VALIDATION_RULES.dosageInstructions.min) {
        newErrors.dosageInstructions = `Dosage Instructions must be at least ${VALIDATION_RULES.dosageInstructions.min} characters`;
      } else if (dosageLen > VALIDATION_RULES.dosageInstructions.max) {
        newErrors.dosageInstructions = `Dosage Instructions cannot exceed ${VALIDATION_RULES.dosageInstructions.max} characters`;
      }
    }

    // Vaccination Type validation (required for vaccination records)
    if (formData.recordType === 'VACCINATION' && !formData.vaccinationType.trim()) {
      newErrors.vaccinationType = 'Vaccination Type is required for vaccination records';
    } else if (formData.vaccinationType) {
      const vaccLen = formData.vaccinationType.trim().length;
      if (vaccLen < VALIDATION_RULES.vaccinationType.min) {
        newErrors.vaccinationType = `Vaccination Type must be at least ${VALIDATION_RULES.vaccinationType.min} characters`;
      } else if (vaccLen > VALIDATION_RULES.vaccinationType.max) {
        newErrors.vaccinationType = `Vaccination Type cannot exceed ${VALIDATION_RULES.vaccinationType.max} characters`;
      }
    }

    // Performed By validation
    if (formData.performedBy) {
      const perfLen = formData.performedBy.trim().length;
      if (perfLen < VALIDATION_RULES.performedBy.min) {
        newErrors.performedBy = `Performed By must be at least ${VALIDATION_RULES.performedBy.min} characters`;
      } else if (perfLen > VALIDATION_RULES.performedBy.max) {
        newErrors.performedBy = `Performed By cannot exceed ${VALIDATION_RULES.performedBy.max} characters`;
      }
    }

    // Notes validation (optional but if provided, check length)
    if (formData.notes) {
      const notesLen = formData.notes.trim().length;
      if (notesLen > VALIDATION_RULES.notes.max) {
        newErrors.notes = `Notes cannot exceed ${VALIDATION_RULES.notes.max} characters`;
      }
    }

    // Next Checkup Date must be after Record Date
    if (formData.nextCheckupDate && formData.recordDate) {
      if (formData.nextCheckupDate <= formData.recordDate) {
        newErrors.nextCheckupDate = 'Next Checkup Date must be after the Record Date';
      }
    }

    // Next Vaccination Date must be after Record Date
    if (formData.nextVaccinationDate && formData.recordDate) {
      if (formData.nextVaccinationDate <= formData.recordDate) {
        newErrors.nextVaccinationDate = 'Next Vaccination Date must be after the Record Date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({
        type: 'error',
        text: 'Please fill in all required fields.'
      });
      return;
    }

    if (!gaushalaId) {
      setMessage({
        type: 'error',
        text: 'Gaushala ID not found. Please login again.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      // Convert date to YYYY-MM-DD format for backend LocalDate
      // Build object with only non-empty/non-undefined fields to avoid validation errors
      const healthRecordData: any = {
        cattleId: formData.cattleId,
        gaushalaId: gaushalaId,
        recordType: formData.recordType,
        recordDate: formData.recordDate, // Already in YYYY-MM-DD from date input
        status: formData.status
      };

      // Add optional fields only if they have values
      if (formData.veterinarianName?.trim()) {
        healthRecordData.veterinarianName = formData.veterinarianName.trim();
      }
      if (formData.veterinarianLicense?.trim()) {
        healthRecordData.veterinarianLicense = formData.veterinarianLicense.trim();
      }
      if (formData.veterinarianContact?.trim()) {
        healthRecordData.veterinarianContact = formData.veterinarianContact.trim();
      }
      if (formData.diagnosis?.trim()) {
        healthRecordData.diagnosis = formData.diagnosis.trim();
      }
      if (formData.treatment?.trim()) {
        healthRecordData.treatment = formData.treatment.trim();
      }
      if (formData.medications?.trim()) {
        healthRecordData.medications = formData.medications.trim();
      }
      if (formData.dosageInstructions?.trim()) {
        healthRecordData.dosageInstructions = formData.dosageInstructions.trim();
      }
      if (formData.vaccinationType?.trim()) {
        healthRecordData.vaccinationType = formData.vaccinationType.trim();
      }
      if (formData.nextVaccinationDate?.trim()) {
        healthRecordData.nextVaccinationDate = formData.nextVaccinationDate.trim();
      }
      if (formData.nextCheckupDate?.trim()) {
        healthRecordData.nextCheckupDate = formData.nextCheckupDate.trim();
      }
      if (formData.cost > 0) {
        healthRecordData.cost = formData.cost;
      }
      if (formData.notes?.trim()) {
        healthRecordData.notes = formData.notes.trim();
      }
      if (formData.performedBy?.trim()) {
        healthRecordData.performedBy = formData.performedBy.trim();
      }

      console.log('[HealthRecord] Submitting:', healthRecordData);
      const result = await healthRecordsApi.createHealthRecord(healthRecordData);

      if (result.success) {
        setMessage({
          type: 'success',
          text: result.message || 'Health record created successfully!'
        });

        // Navigate back after delay
        setTimeout(() => {
          navigate('/gaushala/health-history');
        }, 2000);
      } else {
        // Check if error indicates server is unavailable
        const errorText = result.error || '';
        const isServerError = errorText.includes('500') ||
                             errorText.includes('503') ||
                             errorText.includes('status: 500') ||
                             errorText.includes('Internal Server Error') ||
                             !navigator.onLine;

        if (isServerError) {
          console.log('📴 [HEALTH RECORD] Server error detected - attempting offline save');

          try {
            const selectedCattle = cattle.find(c => c.id === formData.cattleId);

            const healthRecordDataForOffline: any = {
              cattleId: formData.cattleId,
              cattleName: selectedCattle?.name || `Cattle #${formData.cattleId}`,
              gaushalaId: gaushalaId,
              recordType: formData.recordType,
              recordDate: formData.recordDate,
              status: formData.status,
              veterinarianName: formData.veterinarianName?.trim() || undefined,
              veterinarianLicense: formData.veterinarianLicense?.trim() || undefined,
              veterinarianContact: formData.veterinarianContact?.trim() || undefined,
              diagnosis: formData.diagnosis?.trim() || undefined,
              treatment: formData.treatment?.trim() || undefined,
              medications: formData.medications?.trim() || undefined,
              dosageInstructions: formData.dosageInstructions?.trim() || undefined,
              vaccinationType: formData.vaccinationType?.trim() || undefined,
              nextVaccinationDate: formData.nextVaccinationDate?.trim() || undefined,
              nextCheckupDate: formData.nextCheckupDate?.trim() || undefined,
              cost: formData.cost > 0 ? formData.cost : undefined,
              notes: formData.notes?.trim() || undefined,
              performedBy: formData.performedBy?.trim() || undefined
            };

            const offlineResult = await offlineHealthRecordApi.create(healthRecordDataForOffline);

            if (offlineResult.success) {
              setMessage({
                type: 'success',
                text: '📴 Server unavailable - Health record saved locally! Will sync when online.'
              });
              setTimeout(() => navigate('/gaushala/health-history'), 2500);
              return;
            }
          } catch (offlineError) {
            console.error('[HEALTH RECORD] Offline save failed:', offlineError);
          }
        }

        setMessage({
          type: 'error',
          text: result.error || 'Failed to create health record.'
        });
      }
    } catch (error: any) {
      console.error('Error creating health record:', error);

      // Extract detailed error message from different error sources
      let errorMessage = 'Failed to create health record. Please try again.';

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // ============== FALLBACK TO OFFLINE SAVING ==============
      const isServerError = errorMessage.includes('500') ||
                           errorMessage.includes('Internal Server Error') ||
                           errorMessage.includes('Network') ||
                           errorMessage.includes('fetch');

      if (isServerError) {
        console.log('📴 [HEALTH RECORD] Server unavailable - attempting offline save');
        try {
          const healthRecordDataForOffline: any = {
            cattleId: formData.cattleId,
            gaushalaId: gaushalaId,
            recordType: formData.recordType,
            recordDate: formData.recordDate,
            status: formData.status
          };

          // Add optional fields only if they have values
          if (formData.veterinarianName?.trim()) healthRecordDataForOffline.veterinarianName = formData.veterinarianName.trim();
          if (formData.veterinarianLicense?.trim()) healthRecordDataForOffline.veterinarianLicense = formData.veterinarianLicense.trim();
          if (formData.veterinarianContact?.trim()) healthRecordDataForOffline.veterinarianContact = formData.veterinarianContact.trim();
          if (formData.diagnosis?.trim()) healthRecordDataForOffline.diagnosis = formData.diagnosis.trim();
          if (formData.treatment?.trim()) healthRecordDataForOffline.treatment = formData.treatment.trim();
          if (formData.medications?.trim()) healthRecordDataForOffline.medications = formData.medications.trim();
          if (formData.dosageInstructions?.trim()) healthRecordDataForOffline.dosageInstructions = formData.dosageInstructions.trim();
          if (formData.vaccinationType?.trim()) healthRecordDataForOffline.vaccinationType = formData.vaccinationType.trim();
          if (formData.nextVaccinationDate?.trim()) healthRecordDataForOffline.nextVaccinationDate = formData.nextVaccinationDate.trim();
          if (formData.nextCheckupDate?.trim()) healthRecordDataForOffline.nextCheckupDate = formData.nextCheckupDate.trim();
          if (formData.cost > 0) healthRecordDataForOffline.cost = formData.cost;
          if (formData.notes?.trim()) healthRecordDataForOffline.notes = formData.notes.trim();
          if (formData.performedBy?.trim()) healthRecordDataForOffline.performedBy = formData.performedBy.trim();

          const offlineResult = await offlineHealthRecordApi.create(healthRecordDataForOffline);

          if (offlineResult.success) {
            console.log('✅ [HEALTH RECORD] Saved offline with temp ID:', offlineResult.tempId);
            setMessage({
              type: 'success',
              text: 'Server unavailable - Health record saved locally! Will sync automatically when server reconnects.'
            });
            setTimeout(() => {
              navigate('/gaushala/health-history');
            }, 2500);
            return;
          }
        } catch (offlineError) {
          console.error('❌ [HEALTH RECORD] Offline save also failed:', offlineError);
        }
      }

      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/gaushala/health-history');
  };

  // Searchable Select Field Component (still inside because it's unique to this form)
  const SearchableSelectField = ({
    label,
    value,
    onChange,
    placeholder,
    required = false,
    error,
    options,
    className = ''
  }: any) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const isOpenRef = useRef(false);

    // Get selected option name
    const selectedOption = options.find((opt: any) => opt.id === value);
    const displayValue = selectedOption ? selectedOption.name : placeholder;

    // Filter options based on search term
    const filteredOptions = options.filter((option: any) =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Focus search input when dropdown opens (only once)
    useEffect(() => {
      if (isOpen && searchInputRef.current && !isOpenRef.current) {
        isOpenRef.current = true;
        searchInputRef.current?.focus();
      } else if (!isOpen) {
        isOpenRef.current = false;
      }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          setSearchTerm('');
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <div className={`space-y-2 ${className}`} ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-800">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          {/* Selected Value Display */}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
              error ? 'border-red-300 bg-red-50' : 'border-gray-300'
            } ${!selectedOption ? 'text-gray-400' : 'text-gray-900'}`}
          >
            <span className="truncate">{displayValue}</span>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
              {/* Search Input */}
              <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>

              {/* Options List */}
              <div className="max-h-60 overflow-y-auto">
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-8 text-center text-sm text-gray-500">
                    No options found
                  </div>
                ) : (
                  filteredOptions.map((option: any) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        onChange(option.id);
                        setIsOpen(false);
                        setSearchTerm('');
                      }}
                      className={`w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors ${
                        option.id === value ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-900'
                      }`}
                    >
                      {option.name}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <span className="text-red-500">⚠</span>
            {error}
          </div>
        )}
      </div>
    );
  };

  const SelectField = ({
    label,
    value,
    onChange,
    placeholder,
    required = false,
    error,
    options,
    className = ''
  }: any) => (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-800">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300'
        }`}
      >
        <option value="">{placeholder}</option>
        {options.map((option: any) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );

  // Show loading state while fetching dropdown data
  if (dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Loading form data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add Health Record</h1>
          <p className="text-gray-600 mt-1">Record new health checkup or treatment</p>
        </div>
        <button
          onClick={handleCancel}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? (
              <span className="text-green-500">✓</span>
            ) : (
              <span className="text-red-500">⚠</span>
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
            <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
              <Heart className="h-5 w-5 text-red-600" />
            </div>
            Create New Health Record
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cattle - Searchable */}
            <SearchableSelectField
              label="Cattle"
              value={formData.cattleId}
              onChange={(value: number) => handleInputChange('cattleId', value)}
              placeholder="Search and select cattle"
              required
              error={errors.cattleId}
              options={cattle}
            />

            {/* Record Type */}
            <SelectField
              label="Record Type"
              value={formData.recordType}
              onChange={(value: string) => handleInputChange('recordType', value)}
              placeholder="Select record type"
              required
              error={errors.recordType}
              options={[
                { id: 'VACCINATION', name: 'Vaccination' },
                { id: 'TREATMENT', name: 'Treatment' },
                { id: 'CHECKUP', name: 'Checkup' },
                { id: 'SURGERY', name: 'Surgery' },
                { id: 'INJURY', name: 'Injury' }
              ]}
            />

            {/* Record Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Record Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.recordDate}
                  onChange={(e) => handleInputChange('recordDate', e.target.value)}
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                    errors.recordDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.recordDate && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.recordDate}
                </div>
              )}
            </div>

            {/* Status */}
            <SelectField
              label="Status"
              value={formData.status}
              onChange={(value: string) => handleInputChange('status', value)}
              placeholder="Select status"
              required
              error={errors.status}
              options={[
                { id: 'SCHEDULED', name: 'Scheduled' },
                { id: 'COMPLETED', name: 'Completed' },
                { id: 'CANCELLED', name: 'Cancelled' }
              ]}
            />

            {/* Veterinarian Name */}
            <InputField
              label="Veterinarian Name"
              value={formData.veterinarianName}
              onChange={(value: string) => handleInputChange('veterinarianName', value)}
              placeholder="Enter veterinarian name (min 2 characters)"
              maxLength={VALIDATION_RULES.veterinarianName.max}
              error={errors.veterinarianName}
            />

            {/* Veterinarian License */}
            <InputField
              label="Veterinarian License"
              value={formData.veterinarianLicense}
              onChange={(value: string) => handleInputChange('veterinarianLicense', value)}
              placeholder="Enter license number"
              maxLength={VALIDATION_RULES.veterinarianLicense.max}
              error={errors.veterinarianLicense}
            />

            {/* Veterinarian Contact */}
            <InputField
              label="Veterinarian Contact"
              value={formData.veterinarianContact}
              onChange={(value: string) => handleInputChange('veterinarianContact', value)}
              type="text"
              placeholder="Enter 10-digit contact number"
              maxLength={10}
              isPhoneNumber={true}
              error={errors.veterinarianContact}
            />

            {/* Performed By */}
            <InputField
              label="Performed By"
              value={formData.performedBy}
              onChange={(value: string) => handleInputChange('performedBy', value)}
              placeholder="Enter who performed the procedure"
              maxLength={VALIDATION_RULES.performedBy.max}
              error={errors.performedBy}
            />

            {/* Vaccination Type - Show only for VACCINATION */}
            {formData.recordType === 'VACCINATION' && (
              <InputField
                label="Vaccination Type"
                value={formData.vaccinationType}
                onChange={(value: string) => handleInputChange('vaccinationType', value)}
                placeholder="Enter vaccination type"
                required
                error={errors.vaccinationType}
                maxLength={VALIDATION_RULES.vaccinationType.max}
              />
            )}

            {/* Next Vaccination Date */}
            {formData.recordType === 'VACCINATION' && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-800">
                  Next Vaccination Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={formData.nextVaccinationDate}
                    onChange={(e) => handleInputChange('nextVaccinationDate', e.target.value)}
                    min={formData.recordDate ? (() => { const d = new Date(formData.recordDate); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })() : undefined}
                    className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                      errors.nextVaccinationDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                {errors.nextVaccinationDate && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <span className="text-red-500">⚠</span>
                    {errors.nextVaccinationDate}
                  </div>
                )}
              </div>
            )}

            {/* Next Checkup Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Next Checkup Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.nextCheckupDate}
                  onChange={(e) => handleInputChange('nextCheckupDate', e.target.value)}
                  min={formData.recordDate ? (() => { const d = new Date(formData.recordDate); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })() : undefined}
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
                    errors.nextCheckupDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.nextCheckupDate && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.nextCheckupDate}
                </div>
              )}
            </div>

            {/* Cost */}
            <CostField
              label="Cost (₹)"
              value={formData.cost}
              onChange={(value: number) => handleInputChange('cost', value)}
              error={errors.cost}
            />

            {/* Diagnosis */}
            <TextAreaField
              label="Diagnosis"
              value={formData.diagnosis}
              onChange={(value: string) => handleInputChange('diagnosis', value)}
              placeholder="Enter diagnosis details (min 5 characters)"
              maxLength={VALIDATION_RULES.diagnosis.max}
              error={errors.diagnosis}
              className="md:col-span-2"
            />

            {/* Treatment */}
            <TextAreaField
              label="Treatment"
              value={formData.treatment}
              onChange={(value: string) => handleInputChange('treatment', value)}
              placeholder="Enter treatment details (min 5 characters)"
              maxLength={VALIDATION_RULES.treatment.max}
              error={errors.treatment}
              className="md:col-span-2"
            />

            {/* Medications */}
            <TextAreaField
              label="Medications"
              value={formData.medications}
              onChange={(value: string) => handleInputChange('medications', value)}
              placeholder="Enter medications prescribed (min 5 characters)"
              maxLength={VALIDATION_RULES.medications.max}
              error={errors.medications}
            />

            {/* Dosage Instructions */}
            <TextAreaField
              label="Dosage Instructions"
              value={formData.dosageInstructions}
              onChange={(value: string) => handleInputChange('dosageInstructions', value)}
              placeholder="Enter dosage instructions (min 5 characters)"
              maxLength={VALIDATION_RULES.dosageInstructions.max}
              error={errors.dosageInstructions}
            />

            {/* Notes */}
            <TextAreaField
              label="Notes"
              value={formData.notes}
              onChange={(value: string) => handleInputChange('notes', value)}
              placeholder="Enter any additional notes (optional)"
              maxLength={VALIDATION_RULES.notes.max}
              error={errors.notes}
              className="md:col-span-2"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-start gap-4 mt-8 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="h-4 w-4" />
              {isLoading ? 'Creating...' : 'Create Health Record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
